import CheckCircleIcon from "@redotech/redo-icons/check-circle.svg";
import CheckCircleFilledIcon from "@redotech/redo-icons/check-circle_filled.svg";
import FileIcon from "@redotech/redo-icons/file-06.svg";
import FileFilledIcon from "@redotech/redo-icons/file-06_filled.svg";
import RouteIcon from "@redotech/redo-icons/route.svg";
import RouteFilledIcon from "@redotech/redo-icons/route_filled.svg";
import TruckIcon from "@redotech/redo-icons/truck-02.svg";
import TruckFilledIcon from "@redotech/redo-icons/truck-02_filled.svg";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import classNames from "classnames";
import React, { memo } from "react";
import * as styles from "./freight-booking-stepper.module.css";
import {
  FreightBookingStep,
  freightBookingStepLabels,
} from "./freight-booking-types";

const stepIcons: Record<
  FreightBookingStep,
  {
    outline: (props: any) => React.ReactElement;
    filled: (props: any) => React.ReactElement;
  }
> = {
  route: { outline: RouteIcon, filled: RouteFilledIcon },
  freight: { outline: TruckIcon, filled: TruckFilledIcon },
  quotes: { outline: FileIcon, filled: FileFilledIcon },
  book: { outline: CheckCircleIcon, filled: CheckCircleFilledIcon },
};

interface FreightBookingStepperProps {
  steps: readonly FreightBookingStep[];
  currentStep: FreightBookingStep;
}

export const FreightBookingStepper = memo(function FreightBookingStepper({
  steps,
  currentStep,
}: FreightBookingStepperProps) {
  const currentIndex = steps.indexOf(currentStep);

  return (
    <Flex align="center" dir="column" gap="xs">
      <Flex align="center" gap="none">
        {steps.map((step, index) => {
          const isActive = index === currentIndex;
          const isCompleted = index < currentIndex;

          return (
            <React.Fragment key={step}>
              {index > 0 && (
                <Flex
                  className={classNames(styles.connector, {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    [styles.connectorCompleted!]: index <= currentIndex,
                  })}
                />
              )}
              <Flex
                align="center"
                className={classNames(styles.circle, {
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  [styles.active!]: isActive,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  [styles.completed!]: isCompleted,
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  [styles.pending!]: !isActive && !isCompleted,
                })}
                justify="center"
              >
                <RedoIcon
                  className={styles.iconStrokeOverride}
                  color={isActive || isCompleted ? "white" : "tertiary"}
                  Icon={
                    isActive ? stepIcons[step].filled : stepIcons[step].outline
                  }
                  size={12}
                />
              </Flex>
            </React.Fragment>
          );
        })}
      </Flex>
      <Flex align="center" gap="none">
        {steps.map((step, index) => {
          const isActive = index === currentIndex;

          return (
            <React.Fragment key={step}>
              {index > 0 && <Flex className={styles.connectorSpacer} />}
              <Flex className={styles.labelContainer} justify="center">
                <Text
                  fontSize="xs"
                  fontWeight={isActive ? "semibold" : "regular"}
                  textColor={isActive ? "primary" : "secondary"}
                >
                  {freightBookingStepLabels[step]}
                </Text>
              </Flex>
            </React.Fragment>
          );
        })}
      </Flex>
    </Flex>
  );
});
